import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Button,
  LegacyCard,
  FormLayout,
  Icon,
  Page,
  LegacyStack,
  TextField,
  Layout,
  Select,
  PageActions,
  IndexTable,
  EmptyState,
  IndexFilters,
  useSetIndexFiltersMode,
} from "@shopify/polaris";
import { XIcon, ImportIcon, ExportIcon } from "@shopify/polaris-icons";
import AddVariantDialog from "../common/AddVariantDialog";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import HorseVariant from "../common/HorseVariant/HorseVariant";
import {
  deleteSupplier,
  getSupplierHorseVariants,
  removeSupplierHorseVariant,
  removeAllSupplierHorseVariants,
  updateSupplierSelection,
  updateSupplier,
  importSupplierHorseVariants,
  downloadFile,
  getSupplier,
  useUser,
  useCurrencies,
  useSupplierHorseVariantSelectedIds,
} from "../../api_utils/requests";
import { ErrorBanner } from "../common/ErrorBanner";
import { ImportFile } from "../common/ImportFile";
import { Footer } from "../common/Footer";
import {
  assertString,
  cloneNestedObjects,
  extractMessageFromError,
  getCurrencySymbol,
  getIdFromPath,
  softAssertNumber,
} from "../../helper_functions/utils";
import type { QueryParams, Supplier, SupplierHorseVariant } from "../../api_utils/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import type { SortButtonChoice } from "@shopify/polaris";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import { SupplierShowPageSkeleton } from "../common/skeletons";
import { SUPPLIERS_HELP_PAGE_URL } from "../../constants";
import SaveBar from "../common/SaveBar";
import { useNavigate } from "react-router-dom";
import type { QueryKey } from "@tanstack/react-query";

type SupplierHorseVariantWithSortIndex = SupplierHorseVariant & { sortIndex: number };
type SupplierHorseVariants = Record<number, SupplierHorseVariantWithSortIndex>;

const formatSupplierToSave = ({
  name,
  email,
  currency,
  notes,
  minimum_order_value,
  delivery_lead_days,
}: Supplier): Partial<Supplier> => ({
  name,
  email,
  currency,
  notes,
  minimum_order_value,
  delivery_lead_days: delivery_lead_days === 0 ? undefined : delivery_lead_days,
});

const formatSupplierHorseVariants = (supplierHorseVariants: SupplierHorseVariant[]): SupplierHorseVariants =>
  supplierHorseVariants.reduce<SupplierHorseVariants>((map, horseVariant, sortIndex) => {
    map[horseVariant.id] = { ...horseVariant, sortIndex };
    return map;
  }, {});

const columnNames: NonEmptyArray<IndexTableHeading> = [
  { title: "Variant" },
  { title: "Minimum order quantity" },
  { title: "Pack size", tooltipContent: "Case quantity" },
  { title: "Supplier SKU" },
  { title: "Supplier cost" },
  { title: "" },
];

const sortOptions = [
  { label: "Created at", directionLabel: "Oldest/Newest", value: "created_at asc" },
  { label: "Created at", directionLabel: "Newest/Oldest", value: "created_at desc" },
  { label: "Title", directionLabel: "A-Z", value: "title asc" },
  { label: "Title", directionLabel: "Z-A", value: "title desc" },
  { label: "MOQ", directionLabel: "Low/High", value: "minimum_order_quantity asc" },
  { label: "MOQ", directionLabel: "High/Low", value: "minimum_order_quantity desc" },
  { label: "Pack size", directionLabel: "Low/High", value: "pack_size asc" },
  { label: "Pack size", directionLabel: "High/Low", value: "pack_size desc" },
  { label: "Supplier SKU", directionLabel: "A-Z", value: "supplier_sku asc" },
  { label: "Supplier SKU", directionLabel: "Z-A", value: "supplier_sku desc" },
  { label: "Supplier cost", directionLabel: "Low/High", value: "cost asc" },
  { label: "Supplier cost", directionLabel: "High/Low", value: "cost desc" },
] as SortButtonChoice[];

function ShowSupplier(): React.JSX.Element {
  const supplierId = useMemo(() => getIdFromPath(), []);

  const [importCSVModalIsOpen, setImportCSVModalIsOpen] = useState(false);

  const [supplier, setSupplier] = useState<Supplier>({} as Supplier);
  const originalSupplier = useRef<Supplier>();

  const originalSupplierHorseVariants = useRef<SupplierHorseVariants>({} as SupplierHorseVariants);
  const [supplierHorseVariants, setSupplierHorseVariants] = useState<SupplierHorseVariants>(
    {} as SupplierHorseVariants,
  );

  const [pageLoading, setPageLoading] = useState(true);
  const [variantsLoading, setVariantsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    shopify.loading(loading);
  }, [loading]);
  const [isDirty, setIsDirty] = useState(false);
  const [deletionDialogueActive, setDeletionDialogueActive] = useState(false);

  const [addVariantModalActive, setAddVariantModalActive] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  // Pagination for SupplierHorseVariants
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [hasPrev, setHasPrev] = useState<boolean>(false);
  const urlParams = new URLSearchParams(window.location.search);
  const [pageNum, setPageNum] = useState<number>(Number(urlParams.get("page")) || 1);
  const initSortedCol = urlParams.get("by_sort[column]") || "created_at";
  const initSortedColDir = urlParams.get("by_sort[direction]") || "desc";
  const [sort, setSort] = useState([`${initSortedCol} ${initSortedColDir}`]);
  const { mode, setMode } = useSetIndexFiltersMode();

  const setSupplierHorseVariantsData = (
    newSupplierHorseVariants: SupplierHorseVariant[],
    newHasNext: boolean,
    newHasPrev: boolean,
  ): void => {
    const formattedSupplierHorseVariants = formatSupplierHorseVariants(newSupplierHorseVariants || []);
    setSupplierHorseVariants(formattedSupplierHorseVariants);
    originalSupplierHorseVariants.current = cloneNestedObjects(formattedSupplierHorseVariants);
    setHasNext(newHasNext);
    setHasPrev(newHasPrev);
  };

  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();

  const { data: currencies } = useCurrencies();

  useEffect(() => {
      getSupplier(supplierId)
      .then(
        ({ supplier: newSupplier }) => {
          setSupplier(newSupplier);
          originalSupplier.current = {...newSupplier};

          setPageLoading(false);
        },
      )
      .catch((error: unknown) => {
        console.error("Error fetching data:", error);
      });
  }, [supplierId]);

  useEffect(() => {
    const [column, direction] = sort[0].split(" ");
      getSupplierHorseVariants(supplierId, { page: pageNum, by_sort: { column, direction } })
      .then(
        ({ rows: newSupplierHorseVariants, hasNext: newHasNext, hasPrev: newHasPrev }) => {
          setSupplierHorseVariantsData(newSupplierHorseVariants, newHasNext, newHasPrev);

          setVariantsLoading(false);
        },
      )
      .catch((error: unknown) => {
        console.error("Error fetching data:", error);
      });
  }, [supplierId, pageNum, sort]);

  const handleVariantUpdate = useCallback(async () => {
    try {
      const [column, direction] = sort[0].split(" ");
      const {
        rows: newSupplierHorseVariants,
        hasNext: newHasNext,
        hasPrev: newHasPrev,
      } = await getSupplierHorseVariants(supplierId, { page: pageNum, by_sort: { column, direction } });

      const existingVariants = { ...supplierHorseVariants };
      const newVariantsMap = formatSupplierHorseVariants(newSupplierHorseVariants);

      // Add only new variants (not already in existingVariants)
      Object.entries(newVariantsMap).forEach(([horseVariantId, variant]) => {
        if (!existingVariants[horseVariantId]) {
          existingVariants[horseVariantId] = variant;
        }
      });

      // Remove variants that are no longer in the API response (deselected)
      Object.keys(existingVariants).forEach((horseVariantId) => {
        if (!newVariantsMap[horseVariantId]) {
          delete existingVariants[horseVariantId];
        }
      });

      // Use the new sorting
      Object.keys(newVariantsMap).forEach((horseVariantId) => {
        existingVariants[horseVariantId] = {
          ...existingVariants[horseVariantId],
          sortIndex: newVariantsMap[horseVariantId].sortIndex,
        };
      });

      // Merge new variants with existing ones, avoiding duplicates by horse_variant_id
      setSupplierHorseVariants(existingVariants);

      // Update original variants
      originalSupplierHorseVariants.current = newVariantsMap;

      setHasNext(newHasNext);
      setHasPrev(newHasPrev);
    } catch (err: unknown) {
      const message = extractMessageFromError(err);
      setErrorMessage(message);
      console.error("Error in handleVariantUpdate:", message);
    }
  }, [supplierId, pageNum, sort, supplierHorseVariants]);

  const handleSupplierChange = useCallback((value: string, key: string): void => {
    setSupplier((prevSupplier) => {
      return { ...prevSupplier, [key]: value };
    });
    setIsDirty(true);
  }, []);

  const handleSupplierNumberChange = useCallback((value: string, key: string): void => {
    const cleanValue = value?.replace(/[^\d.-]/g, "");
    setSupplier((prevSupplier) => {
      return { ...prevSupplier, [key]: cleanValue };
    });
    setIsDirty(true);
  }, []);

  const handleSaveSupplier = useCallback(() => {
    setLoading(true);
    const formattedSupplier = formatSupplierToSave(supplier);
    const payload = {
      supplier: {
        ...formattedSupplier,
        supplier_horse_variants_attributes: Object.values(supplierHorseVariants).map(({ sortIndex, ...variant }) => ({
          ...variant,
          pack_size: variant.pack_size || null,
          minimum_order_quantity: variant.minimum_order_quantity || 0,
          supplier_sku: variant.supplier_sku || null,
          cost: variant.cost || null,
        })),
      },
    };
    updateSupplier(supplierId, payload)
      .then((res) => {
        shopify.toast.show("Save successful");
        setIsDirty(false);
        setSupplier(res.supplier);
        originalSupplier.current = {...res.supplier};

        originalSupplierHorseVariants.current = cloneNestedObjects(supplierHorseVariants);
      })
      .catch((err: unknown) => {
        Rollbar.error(err, payload);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [supplier, supplierHorseVariants, supplierId]);

  const handleHorseVariantsChange = useCallback(
    (variantId: number, value: number | string, key: string) => {
      const newSupplierHorseVariants = { ...supplierHorseVariants };
      newSupplierHorseVariants[variantId] = {
        ...newSupplierHorseVariants[variantId],
        [key]: value,
      } as SupplierHorseVariantWithSortIndex;
      setSupplierHorseVariants(newSupplierHorseVariants);
      setIsDirty(true);
    },
    [supplierHorseVariants],
  );

  const removeAllVariants = useCallback(async () => {
    setLoading(true);
    setSupplierHorseVariants({} as SupplierHorseVariants);

    try {
      await removeAllSupplierHorseVariants(supplierId)
      originalSupplierHorseVariants.current = {} as SupplierHorseVariants;
    } catch (err: unknown) {
      setSupplierHorseVariants(cloneNestedObjects(originalSupplierHorseVariants.current));
      Rollbar.error(err, { supplierId });
      const message = extractMessageFromError(err);
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  const removeVariant = useCallback(
    (id: number) => {
      setLoading(true);
      const updated = { ...supplierHorseVariants };
      delete updated[id];
      setSupplierHorseVariants(updated);
      removeSupplierHorseVariant(id)
        .then(() => {
          shopify.toast.show("Variant removed");

          originalSupplierHorseVariants.current = updated;
        })
        .catch((err: unknown) => {
          setSupplierHorseVariants(originalSupplierHorseVariants.current);
 
          Rollbar.error(err, { supplierId, variantId: id });
          const message = extractMessageFromError(err);
          setErrorMessage(message);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [supplierId, supplierHorseVariants],
  );

  const handleDiscardAction = useCallback(() => {
    setSupplier({...originalSupplier.current});
    setSupplierHorseVariants(cloneNestedObjects(originalSupplierHorseVariants.current));
    setIsDirty(false);
  }, [originalSupplier, originalSupplierHorseVariants]);

  const setPageNumAndScroll = useCallback(
    async (newPageNum: number) => {
      setLoading(true);
      setPageNum(newPageNum);
      const [column, direction] = sort[0].split(" ");
      await getSupplierHorseVariants(supplierId, { page: newPageNum, by_sort: { column, direction } }).then(
        ({ rows: newSupplierHorseVariants, hasNext: newHasNext, hasPrev: newHasPrev }) => {
          setSupplierHorseVariantsData(newSupplierHorseVariants, newHasNext, newHasPrev);
          setLoading(false);

          document.getElementsByClassName("Polaris-IndexTable__Table")[0]?.scrollIntoView();
        },
      );
    },
    [supplierId, sort],
  );
  const nextPage = useCallback(async () => {
    if (hasNext) {
      await setPageNumAndScroll(pageNum + 1);
    }
  }, [hasNext, pageNum, setPageNumAndScroll]);
  const prevPage = useCallback(async () => {
    if (hasPrev) {
      await setPageNumAndScroll(pageNum - 1);
    }
  }, [hasPrev, pageNum, setPageNumAndScroll]);

  const emptyState = (
    <EmptyState
      action={{
        content: "Add variants",
        onAction: () => {
          setAddVariantModalActive(true);
        },
      }}
      heading="No variants added"
      image=""
    >
      <p>You haven&apos;t added any variants to your supplier yet.</p>
    </EmptyState>
  );

  const selectedIdsQueryKey: QueryKey = ["supplier_horse_variant_selected_ids", supplierId];
  const querySelected = useSupplierHorseVariantSelectedIds(supplierId, {
    enabled: false,
    queryKey: selectedIdsQueryKey,
  });

  const navigate = useNavigate();

  const sortedSupplierHorseVariants = useMemo(() => {
    return Object.values(supplierHorseVariants).sort((left, right) => left.sortIndex - right.sortIndex);
  }, [supplierHorseVariants]);

  const pageMarkup = supplier && (
    <Page
      actionGroups={[
        {
          title: "More actions",
          actions: [
            {
              content: "Export CSV",
              onAction: async (): Promise<void> => {
                const supplierName = supplier.name || supplierId;
                const filename = `Horse Supplier ${supplierName}.csv`;
                await downloadFile(`/suppliers/${supplierId}.csv${window.location.search}`, filename);
              },
              icon: ExportIcon,
            },
            {
              content: "Import CSV",
              onAction: () => {
                setImportCSVModalIsOpen(true);
              },
              icon: ImportIcon,
            },
          ],
        },
      ]}
      backAction={{
        content: "Suppliers",
        // url: "/suppliers",
        onAction: async () => {
          await shopify.saveBar.leaveConfirmation();

          navigate("/suppliers");
        },
      }}
      title={supplier.name}
    >
      <SaveBar handleDiscard={handleDiscardAction} handleSave={handleSaveSupplier} isDirty={isDirty} />

      <ImportFile
        apiCallMethod={async (formData: object) => importSupplierHorseVariants(supplierId, formData)}
        modalIsOpen={importCSVModalIsOpen}
        setApiResponse={(supplier_horse_variants: SupplierHorseVariant[]) => {
          const newSupplierHorseVariants = formatSupplierHorseVariants(supplier_horse_variants);
          setSupplierHorseVariants(newSupplierHorseVariants);
          originalSupplierHorseVariants.current = cloneNestedObjects(newSupplierHorseVariants);
        }}
        setErrorMessage={setErrorMessage}
        setModalIsOpen={setImportCSVModalIsOpen}
        title="Import supplier CSV"
      />

      <Layout>
        {errorMessage ? (
          <Layout.Section variant="fullWidth">
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}
        <Layout.Section>
          <LegacyCard>
            <LegacyCard.Section>
              <FormLayout>
                <TextField
                  autoComplete="on"
                  id="name"
                  label="Name"
                  onChange={handleSupplierChange}
                  value={supplier.name}
                />
                <TextField
                  autoComplete="on"
                  id="email"
                  label="Contact email"
                  onChange={handleSupplierChange}
                  placeholder="contact@example.com"
                  value={assertString(supplier.email)}
                />
                <TextField
                  autoComplete="on"
                  id="notes"
                  label="Purchase order notes"
                  multiline={2}
                  onChange={handleSupplierChange}
                  value={assertString(supplier.notes)}
                />
                <FormLayout.Group condensed>
                  <TextField
                    autoComplete="off"
                    helpText="Used as the default arrival date when creating new purchase orders"
                    id="delivery_lead_days"
                    label="Delivery lead days"
                    min={0}
                    onChange={handleSupplierNumberChange}
                    suffix="days"
                    type="integer"
                    value={assertString(supplier.delivery_lead_days)}
                  />
                  <TextField
                    autoComplete="on"
                    id="minimum_order_value"
                    label="Minimum Order Value"
                    onChange={handleSupplierNumberChange}
                    suffix={supplier.currency || userCurrency}
                    type="number"
                    value={assertString(supplier.minimum_order_value)}
                  />
                  <Select
                    id="currency"
                    label="Currency"
                    onChange={handleSupplierChange}
                    options={currencies}
                    value={supplier.currency || userCurrency}
                  />
                </FormLayout.Group>
              </FormLayout>
            </LegacyCard.Section>

            <LegacyCard.Section title="Add variants">
              <LegacyStack distribution="center">
                <AddVariantDialog
                  active={addVariantModalActive}
                  handleVariantUpdate={handleVariantUpdate}
                  primary
                  queryKey={selectedIdsQueryKey}
                  querySelected={querySelected}
                  removeAllVariants={removeAllVariants}
                  selectionKey="supplier_horse_variants_attributes"
                  setActive={setAddVariantModalActive}
                  setErrorMessage={setErrorMessage}
                  updateSelection={async (payload: object, queryParams: QueryParams) =>
                    await updateSupplierSelection(supplierId, payload, queryParams)
                  }
                />
              </LegacyStack>
            </LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>
        <Layout.Section>
          <LegacyCard>
            <IndexFilters
              disableQueryField
              filters={[]}
              hideFilters
              hideQueryField
              mode={mode}
              onClearAll={() => {}}
              onQueryChange={() => {}}
              onQueryClear={() => {}}
              onSort={setSort}
              selected={0}
              setMode={setMode}
              sortOptions={sortOptions}
              sortSelected={sort}
              tabs={[]}
            />
            <IndexTable
              emptyState={emptyState}
              headings={columnNames}
              itemCount={sortedSupplierHorseVariants.length}
              loading={variantsLoading}
              pagination={{
                hasPrevious: hasPrev,
                previousKeys: [37],
                onPrevious: prevPage,
                hasNext: hasNext,
                nextKeys: [39],
                onNext: nextPage,
              }}
              selectable={false}
            >
              {sortedSupplierHorseVariants.map((variant: SupplierHorseVariantWithSortIndex, index) => (
                <IndexTable.Row id={variant.id.toString()} key={variant.id} position={index}>
                  <IndexTable.Cell>
                    <div className="medium_hv">
                      <HorseVariant key={variant.id} variant={variant} />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={variant.discarded}
                        id={`minimum_order_quantity_${variant.id}`}
                        label="Minimum order quantity"
                        labelHidden
                        min={1}
                        name="Minimum order quantity"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.id, softAssertNumber(value), "minimum_order_quantity");
                        }}
                        type="integer"
                        value={assertString(variant.minimum_order_quantity)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={variant.discarded}
                        id={`pack_size_${variant.id}`}
                        label="Pack size"
                        labelHidden
                        min={0}
                        name="Pack size"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.id, softAssertNumber(value), "pack_size");
                        }}
                        type="integer"
                        value={assertString(variant.pack_size)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={variant.discarded}
                        id={`supplier_sku_${variant.id}`}
                        label="Supplier SKU"
                        labelHidden
                        name="Supplier SKU"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.id, value, "supplier_sku");
                        }}
                        value={assertString(variant.supplier_sku)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div className="w120">
                      <TextField
                        autoComplete="off"
                        disabled={variant.discarded}
                        id={`cost_${variant.id}`}
                        label="Supplier cost"
                        labelHidden
                        min={0.0}
                        name="Supplier cost"
                        onChange={(value: string) => {
                          handleHorseVariantsChange(variant.id, value, "cost");
                        }}
                        prefix={getCurrencySymbol(supplier.currency || userCurrency)}
                        step={0.01}
                        type="currency"
                        value={assertString(variant.cost)}
                      />
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Button
                      icon={<Icon source={XIcon} />}
                      onClick={() => {
                        removeVariant(variant.id);
                      }}
                      variant="plain"
                    />
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </LegacyCard>
        </Layout.Section>
      </Layout>
      <PageActions
        secondaryActions={[
          {
            content: "Delete",
            destructive: true,
            onAction: () => {
              setDeletionDialogueActive(true);
            },
          },
        ]}
      />
      <DeleteConfirmationDialog
        active={deletionDialogueActive}
        content="Are you sure you want to delete this supplier? This action cannot be reversed."
        deleteItem={deleteSupplier}
        gotoPath="/suppliers"
        itemId={supplierId}
        setActive={setDeletionDialogueActive}
        title="Delete supplier"
        toastMessage="Supplier deleted"
      />
      <Footer pageTitle="suppliers" url={SUPPLIERS_HELP_PAGE_URL} />
    </Page>
  );

  return pageLoading ? <SupplierShowPageSkeleton /> : pageMarkup;
}

export default ShowSupplier;
